import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { useState } from "react";

import {
  copyIcon,
  fbIcon,
  instagramIcon,
  twitchIcon,
  xIcon,
  ytIcon,
} from "../../../designSystemIcons";

type Socials = {
  ytLink?: string;
  xLink?: string;
  twitchLink?: string;
  instagramLink?: string;
  fbLink?: string;
};

export const IconLink = ({ icon, link }) => (
  <a
    className="my-auto opacity-75 hover:opacity-100"
    href={link}
    target="_blank"
    rel="noreferrer"
  >
    {icon}
  </a>
);

interface TacticInfoProps {
  name: string;
  icon: React.ReactNode;
  popularityScore: number;
  author: string;
  tags: string[];
  description: string;
  socials: Socials;
  handleCopy: () => void;
  isSquadBuidler: boolean;
  eaCode?: string;
  noTactic?: boolean;
}

export const TacticInfo = ({
  name,
  icon,
  author,
  tags,
  description,
  socials,
  handleCopy,
  isSquadBuidler,
  eaCode,
  noTactic,
}: TacticInfoProps) => {
  const { ytLink, xLink, twitchLink, instagramLink, fbLink } = socials;
  const anySocials = ytLink || xLink || twitchLink || instagramLink || fbLink;
  const [hoverCopy, setHoverCopy] = useState(false);
  const { t } = useTranslation("tactics");
  return (
    <div
      className={cn(
        "flex flex-col w-full border border-[#636363] rounded-lg mobile:rounded-none sb-v2-lg:rounded-lg gap-y-2 bg-black/30",
      )}
    >
      <div className="flex flex-col">
        <div
          className={cn(
            "flex my-auto gap-x-2 ",
            !noTactic && "bg-white/10 rounded-t-lg",
          )}
        >
          {noTactic ? (
            <img
              alt={t("card.noTacticAlt")}
              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/tactics/logos/no-tactic.png`}
              className=" h-[60px] aspect-square rounded-tl"
            />
          ) : (
            icon
          )}
          <div className={cn("flex flex-col text-left gap-y-1 py-1")}>
            <h2
              className={cn(
                "text-sm font-medium",
                noTactic && "mt-3 text-base",
              )}
            >
              {name}
            </h2>
            {tags.length > 0 && (
              <div className="flex gap-x-1">
                {tags.map((tag) => (
                  <p
                    key={tag}
                    className="py-[1px] px-1 bg-primary-blue rounded text-xs font-semibold"
                  >
                    {tag}
                  </p>
                ))}
              </div>
            )}
            {author && (
              <div className="flex flex-wrap items-center text-xs font-medium gap-x-2 whitespace-nowrap">
                <p className="text-gray-300">
                  {t("card.byAuthor", { author })}
                </p>
                {anySocials && !isSquadBuidler && (
                  <div className=" -mt-0.5 flex">
                    {ytLink && <IconLink icon={ytIcon} link={ytLink} />}
                    {xLink && <IconLink icon={xIcon} link={xLink} />}
                    {twitchLink && (
                      <IconLink icon={twitchIcon} link={twitchLink} />
                    )}
                    {instagramLink && (
                      <IconLink icon={instagramIcon} link={instagramLink} />
                    )}
                    {fbLink && <IconLink icon={fbIcon} link={fbLink} />}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <p className="px-2 text-xs text-gray-300">{description}</p>
      {eaCode && (
        <button
          className={cn(
            "flex p-2 ml-auto gap-x-1 text-sm",
            hoverCopy
              ? "fill-gray-100 text-gray-100"
              : "fill-[#e0e0e0] text-secondary",
          )}
          onClick={handleCopy}
          onMouseEnter={() => setHoverCopy(true)}
          onMouseLeave={() => setHoverCopy(false)}
        >
          <p>{eaCode}</p>
          <span className="mt-0.5">{copyIcon}</span>
        </button>
      )}
    </div>
  );
};
